<template>
  <div v-if="visible" class="modal-overlay" @click.self="close">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h3>Season dates — {{ team?.teamName }}</h3>
            <span
              v-if="isOverriding"
              class="override-badge"
              title="This team has its own dates set instead of using the club default"
            >
              Overriding club default
            </span>
            <span v-else class="inherit-badge">
              Inheriting club default
            </span>
          </div>
          <button class="close-btn" @click="close" aria-label="Close">&times;</button>
        </div>

        <div class="modal-body">
          <p class="help-text">
            Set explicit dates for this team, or use the club's season calendar
            (set in Settings → Season Phases). All season-filtered data
            (games, injuries, wellness, performance) will use whichever applies.
          </p>

          <div class="date-row">
            <label>
              Season start
              <VueDatePicker
                v-model="startDate"
                :enable-time-picker="false"
                format="dd/MM/yyyy"
                :locale="'en-GB'"
                placeholder="Select start date"
                auto-apply
              />
            </label>
            <label>
              Season end
              <VueDatePicker
                v-model="endDate"
                :enable-time-picker="false"
                format="dd/MM/yyyy"
                :locale="'en-GB'"
                placeholder="Select end date"
                auto-apply
              />
            </label>
          </div>

          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>

        <div class="modal-footer">
          <button
            v-if="isOverriding"
            class="btn btn-link"
            :disabled="saving"
            @click="useClubDefault"
          >
            {{ saving ? "Working…" : "Use club default" }}
          </button>
          <div class="footer-spacer"></div>
          <button class="btn btn-secondary" :disabled="saving" @click="close">
            Cancel
          </button>
          <button class="btn btn-primary" :disabled="saving" @click="save">
            {{ saving ? "Saving…" : "Save" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { Teams } from "@/services/teams-service";
import { SeasonPhases } from "@/services/season-phases-service";
import { mapGetters } from "vuex";

export default {
  name: "TeamSeasonDatesEditor",
  components: { VueDatePicker },

  props: {
    team: {
      type: Object,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      startDate: null,
      endDate: null,
      saving: false,
      errorMessage: "",
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    isOverriding() {
      return Boolean(
        this.team && (this.team.season_start_date || this.team.season_end_date)
      );
    },
  },

  watch: {
    team: {
      immediate: true,
      handler(newTeam) {
        this.errorMessage = "";
        this.startDate = newTeam?.season_start_date
          ? new Date(newTeam.season_start_date)
          : null;
        this.endDate = newTeam?.season_end_date
          ? new Date(newTeam.season_end_date)
          : null;
      },
    },
  },

  methods: {
    close() {
      this.$emit("closed");
    },

    async save() {
      this.errorMessage = "";

      if (!this.startDate || !this.endDate) {
        this.errorMessage = "Both start and end dates are required.";
        return;
      }
      if (new Date(this.startDate) >= new Date(this.endDate)) {
        this.errorMessage = "End date must be after start date.";
        return;
      }

      this.saving = true;
      try {
        const response = await Teams.setTeamSeasonOverride(
          this.team.id,
          SeasonPhases.formatDateForDb(this.startDate),
          SeasonPhases.formatDateForDb(this.endDate),
          this.getProfile?.id
        );
        if (response.data && response.data.success) {
          this.$emit("saved", {
            teamId: this.team.id,
            season_start_date: SeasonPhases.formatDateForDb(this.startDate),
            season_end_date: SeasonPhases.formatDateForDb(this.endDate),
          });
          this.close();
        } else {
          this.errorMessage =
            response.data?.error || "Could not save. Please try again.";
        }
      } catch (error) {
        console.error("Failed to save team season dates:", error);
        this.errorMessage =
          error.response?.data?.error || "Could not save. Please try again.";
      } finally {
        this.saving = false;
      }
    },

    async useClubDefault() {
      const confirmed = window.confirm(
        `Clear season dates for "${this.team?.teamName}"? It will inherit the club calendar.`
      );
      if (!confirmed) return;

      this.errorMessage = "";
      this.saving = true;
      try {
        const response = await Teams.clearTeamSeasonOverride(
          this.team.id,
          this.getProfile?.id
        );
        if (response.data && response.data.success) {
          this.$emit("saved", {
            teamId: this.team.id,
            season_start_date: null,
            season_end_date: null,
          });
          this.close();
        } else {
          this.errorMessage =
            response.data?.error || "Could not clear. Please try again.";
        }
      } catch (error) {
        console.error("Failed to clear team season dates:", error);
        this.errorMessage =
          error.response?.data?.error || "Could not clear. Please try again.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-dialog {
  width: 100%;
  max-width: 560px;
  margin: 16px;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  gap: 12px;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
}

.override-badge,
.inherit-badge {
  display: inline-block;
  margin-top: 6px;
  font-size: 0.75rem;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 500;
}

.override-badge {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffe5a0;
}

.inherit-badge {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  color: #6c757d;
  padding: 0 4px;
}

.modal-body {
  padding: 16px 20px;
}

.help-text {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0 0 16px 0;
}

.date-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.date-row label {
  flex: 1;
  min-width: 200px;
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #495057;
}

.error-text {
  margin-top: 12px;
  color: #dc3545;
  font-size: 0.85rem;
}

.modal-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
}

.footer-spacer {
  flex: 1;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #28a745;
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-link {
  background: none;
  color: #007bff;
  padding: 8px 12px;
}

.btn-link:hover:not(:disabled) {
  text-decoration: underline;
}
</style>
